import React from 'react';
import { observer } from 'mobx-react';
import { CaretRightOutlined } from '@ant-design/icons';

import { Section } from 'controls/react/layout/section';
import { Toolbar } from 'controls/react/layout/toolbar';
import { FormEntryNew, HelpIcon } from 'controls/react/form/formEntryNew';
import { AntSelect } from 'controls/react/ant/antSelect';
import { SingleMetricSelector } from 'controls/react/metricsSelector';
import { AntInput, AntTextArea } from 'controls/react/ant/antInput';
import { AntSwitch } from 'controls/react/ant/antSwitch';
import { AntInputNumber } from 'controls/react/ant/antInputNumber';
import { AntButton } from 'controls/react/ant/antButton';
import { AntPopover } from 'controls/react/ant/antPopover';
import { useStore } from 'core/react/useStore';
import { MetricSelectorProps } from 'areas/metrics/components/MetricSelector/props';
import { MetricSelectorStore } from 'areas/metrics/components/MetricSelector/store';
import { MetricConfigurationUnitType } from 'areas/metrics/components/MetricSelector/types';
import { i18n } from 'core/localization';

export const MetricSelector = observer((props: MetricSelectorProps) => {
	const store = useStore(() => new MetricSelectorStore(props), { props });

	const [showAdvanced, setShowAdvanced] = React.useState(store.advancedInitial);

	return (
		<Section appearance={'frame'} childrenPadding={true}>
			<Toolbar title={props.label ?? i18n('Metric')}>
				<AntSwitch
					value={showAdvanced}
					checkedChildren={i18n('Advanced')}
					unCheckedChildren={i18n('Advanced')}
					size={'small'}
					onClick={() => setShowAdvanced(!showAdvanced)}
				/>
			</Toolbar>
			<FormEntryNew label={i18n('Metric')} model={store.model} modelField={'metricId'}>
				<SingleMetricSelector accountId={props.accountId} />
			</FormEntryNew>
			{showAdvanced && (
				<>
					<FormEntryNew
						label={i18n('Unit')}
						disabled={!store.loaded}
						model={store.model}
						modelField={'unit'}
					>
						<AntSelect options={store.units} />
					</FormEntryNew>

					{store.model?.unit == MetricConfigurationUnitType.Custom && (
						<FormEntryNew
							label={i18n('Unit Label')}
							disabled={!store.loaded}
							changeOnBlur={true}
							model={store.model}
							modelField={'unitLabel'}
						>
							<AntInput />
						</FormEntryNew>
					)}

					{store.model?.unit == MetricConfigurationUnitType.Custom && (
						<FormEntryNew
							label={i18n('Formula')}
							disabled={!store.loaded}
							model={store.model}
							changeOnBlur={true}
							headerAdditionalContent={
								<>
									<HelpIcon tooltip={i18n('Formula tooltip')} />
									<AntPopover
										trigger={'contextMenu'}
										open={store.validationResultVisible}
										overlayPadding={true}
										content={store.validationResult}
									>
										<AntButton
											icon={<CaretRightOutlined />}
											onClick={store.validateFormula}
											loading={store.validating}
											disabled={!store.loaded}
											title={i18n('Validate')}
											size={'small'}
											type={'primary'}
										/>
									</AntPopover>
								</>
							}
							modelField={'formula'}
						>
							<AntTextArea
								autoSize={{ minRows: 1, maxRows: 3 }}
								placeholder={i18n('Formula placeholder')}
							/>
						</FormEntryNew>
					)}
					<FormEntryNew
						label={i18n('Decimals')}
						changeOnBlur={true}
						model={props.model}
						modelField={'decimals'}
					>
						<AntInputNumber disabled={!store.loaded} />
					</FormEntryNew>
				</>
			)}
		</Section>
	);
});
